import ExpandableCaption from 'components/common/ExpandableCaption';
import RiveBadge from 'components/common/RiveBadge';
import { ReportIcon } from 'components/common/svgIcons';
import UserButton from 'components/community/shared/UserButton';
import DeleteShotButton from 'components/community/shot/DeleteShotButton';
import ConfirmDeleteCommunityPost from 'components/profile/ConfirmDeleteCommunityPost';
import { UserContext } from 'contexts/user';
import * as analytics from 'lib/analytics';
import { communityDownload, communityReport } from 'lib/analytics';
import { getShareLink } from 'lib/community';
import { generateMailTo } from 'lib/mailto';
import sanitizeHTML from 'lib/sanitizeHTML';
import { formatDate } from 'lib/dateUtils';
import React, { useContext, useState } from 'react';
import { VoidCallback } from 'types/callbacks';
import { CommunityShotItem } from 'types/community';
import RemixModal from '../remix/RemixModal';
import CommentsButton from './CommentsButton';
import MetadataLabel from './MetadataLabel';
import OpenInRiveCommunityButton from './OpenInRiveCommunityButton';
import ParentPosts from './ParentPosts';
import ReactionButton from './ReactionButton';
import RemixButton from './RemixButton';
import RevisionSelect from './RevisionSelect';
import styles from './ShotMetadata.module.css';
import TagLinks from './TagLinks';
import featureFlags from 'lib/featureFlags';
import { navigateTo } from 'lib/utils';
import { join } from 'lib/helpers';
import DownloadButton from './DownloadButton';

type ShotMetadataProps = {
  shotItem: CommunityShotItem;
  children?: React.ReactNode;
  disableReactionsTray?: boolean;
  onCommentsClick: VoidCallback;
};

export default function ShotMetadata({
  shotItem,
  children,
  disableReactionsTray = false,
  onCommentsClick,
}: ShotMetadataProps) {
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [isRemixModalOpen, setIsRemixModalOpen] = useState<boolean>(false);

  const { user } = useContext(UserContext);
  const isOwner = user?.ownerId === shotItem.ownerId;

  const description = sanitizeHTML(shotItem.description);

  const mailToString = generateMailTo({
    to: 'support@rive.app',
    subject: `Community Content Report - ${shotItem.urlSlug}`,
    body: `I am reporting content found on the Rive Community.

URL: ${getShareLink(shotItem)}
Reason:`,
  });

  const { communityPostId, slug, title, fileId } = shotItem;

  function onReportClick() {
    communityReport(communityPostId, slug, title, fileId);
  }

  function onDownloadClick() {
    if (shotItem.fileUrl) {
      const link = document.createElement('a');
      link.href = shotItem.fileUrl;
      link.download = `${slug}.riv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      communityDownload(communityPostId, slug, title, fileId);
    }
  }

  function onRemixClick() {
    if (user?.signedIn) {
      setIsRemixModalOpen(true);
      return;
    }

    const loginUrl = `/login?redirect=${encodeURIComponent(
      window.location.pathname
    )}`;
    // If not signed in, redirect to the login page
    navigateTo(loginUrl);
    analytics.clickCommunityRemix(
      shotItem.communityPostId,
      shotItem.fileId,
      shotItem.title,
      shotItem.isRemix,
      'redirect_to_login'
    );
  }

  const modals = (
    <>
      <ConfirmDeleteCommunityPost
        display={isDeleteModelOpen}
        onCancel={() => setIsDeleteModelOpen(false)}
        shotItem={shotItem}
      />
      <RemixModal
        display={isRemixModalOpen}
        onClose={() => setIsRemixModalOpen(false)}
        shotItem={shotItem}
      />
    </>
  );

  const reportLink = (
    <a
      className={styles.ReportAction}
      href={mailToString}
      onClick={onReportClick}
      aria-label="Report this post"
    >
      <ReportIcon width={10} />
    </a>
  );

  if (featureFlags.newAccountsPage()) {
    return (
      <>
        {modals}
        <section className={join([styles.Card, styles.Details])}>
          <div className={styles.TitleRow}>
            <div className={styles.Title}>
              <h1>{shotItem.title}</h1>
              {shotItem.isRemix && <RiveBadge>REMIX</RiveBadge>}
            </div>
            {reportLink}
          </div>
          <UserButton data={shotItem} />
          <ExpandableCaption>
            <TagLinks className={styles.TagLinks}>{description}</TagLinks>
          </ExpandableCaption>
          <dl className={styles.Facts}>
            <div>
              <dt>License</dt>
              <dd>
                <a href="https://creativecommons.org/licenses/by/4.0/">CC BY</a>
              </dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDate(new Date(shotItem.lastUpdated))}</dd>
            </div>
          </dl>
          {shotItem.parent && (
            <ParentPosts parent={shotItem.parent} root={shotItem.root} />
          )}
        </section>
        <section className={join([styles.Card, styles.Actions])}>
          <button className={styles.Primary} onClick={onRemixClick}>
            Remix in Rive
          </button>
          <div className={styles.ButtonRow}>
            {featureFlags.downloadCommunityShot() && shotItem.fileUrl && (
              <button onClick={onDownloadClick}>Download</button>
            )}
            <OpenInRiveCommunityButton shotItem={shotItem} />
          </div>
          <div className={styles.ButtonRow}>
            <div className={styles.Like}>
              <ReactionButton
                shotItem={shotItem}
                disableReactionsTray={disableReactionsTray}
              />
            </div>
            <CommentsButton shotItem={shotItem} onClick={onCommentsClick} />
          </div>
          {isOwner && (
            <div className={styles.ButtonRow}>
              <DeleteShotButton onClick={() => setIsDeleteModelOpen(true)} />
            </div>
          )}
        </section>
      </>
    );
  }

  return (
    <>
      {modals}
      <div className={styles.ShotMetadataColumn}>
        <div className={styles.ShotMetadata}>
          <div className={styles.MetaContainer}>
            <div
              className={
                featureFlags.dataBinding() && shotItem.isAnimationStateMachine
                  ? styles.CoreMetdataLeva
                  : styles.CoreMetdata
              }
            >
              <div className={styles.Header}>
                <div className={styles.TitleContainer}>
                  <h3>{shotItem.title}</h3>
                  {shotItem.isRemix && (
                    <div className={styles.RemixLabel}>
                      <RiveBadge>REMIX</RiveBadge>
                    </div>
                  )}
                </div>

                {reportLink}
              </div>
              <UserButton className={styles.User} data={shotItem} />
              <ExpandableCaption>
                <TagLinks className={styles.TagLinks}>{description}</TagLinks>
              </ExpandableCaption>
              <div className={styles.MetadataLabels}>
                <MetadataLabel label="License">
                  <a href="https://creativecommons.org/licenses/by/4.0/">
                    CC BY
                  </a>
                </MetadataLabel>
                <MetadataLabel label="Viewing">
                  <RevisionSelect shotItem={shotItem} />
                </MetadataLabel>
              </div>
            </div>
            {shotItem.parent && (
              <ParentPosts parent={shotItem.parent} root={shotItem.root} />
            )}
          </div>
          {children}
        </div>
        <div className={styles.ShotMetadata}>
          <div className={styles.ActionsList}>
            <ReactionButton
              shotItem={shotItem}
              disableReactionsTray={disableReactionsTray}
            />
            <CommentsButton shotItem={shotItem} onClick={onCommentsClick} />
          </div>
        </div>
        <div className={styles.ShotMetadata}>
          <div className={styles.ActionsList}>
            {featureFlags.downloadCommunityShot() && shotItem.fileUrl && (
              <DownloadButton onClick={onDownloadClick} />
            )}
            {<RemixButton shotItem={shotItem} onClick={onRemixClick} />}
            <OpenInRiveCommunityButton shotItem={shotItem} />
          </div>
        </div>
        {isOwner && (
          <div className={styles.ShotMetadata}>
            <div className={styles.ActionsList}>
              <DeleteShotButton onClick={() => setIsDeleteModelOpen(true)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
